import React from 'react';
import { FileText, Download, Share2, MoreVertical } from 'lucide-react';
import { Document } from '../../types';
import { useDeals } from '../../store/DealsContext';

interface DocumentGridProps {
  documents: Document[];
  onSelect: (document: Document) => void;
}

export default function DocumentGrid({ documents, onSelect }: DocumentGridProps) {
  const { deals } = useDeals();

  const getDealName = (dealId: string) => {
    const deal = deals.find(d => d.id === dealId);
    return deal?.name || 'Unassigned';
  };

  return (
    <div className="p-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
      {documents.map((doc) => (
        <div
          key={doc.id}
          className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-center h-32 bg-gray-50 rounded-lg mb-4">
            <FileText className="w-12 h-12 text-gray-400" />
          </div>
          
          <div className="space-y-2">
            <h3 className="text-sm font-medium text-gray-900 truncate">
              {doc.name}
            </h3>
            <div className="flex items-center space-x-2">
              <span className="px-2 py-1 text-xs font-medium rounded-full bg-blue-100 text-blue-800 truncate">
                {getDealName(doc.dealId)}
              </span>
            </div>
            <p className="text-xs text-gray-500">
              Version {doc.version} • {formatBytes(doc.size)}
            </p>
            <p className="text-xs text-gray-500">
              Modified {new Date(doc.lastModified).toLocaleDateString()}
            </p>
          </div>

          <div className="mt-4 flex items-center justify-between">
            <div className="flex space-x-2">
              <button className="p-1 text-gray-400 hover:text-gray-600">
                <Download className="w-4 h-4" />
              </button>
              <button className="p-1 text-gray-400 hover:text-gray-600">
                <Share2 className="w-4 h-4" />
              </button>
            </div>
            <button 
              onClick={() => onSelect(doc)}
              className="p-1 text-gray-400 hover:text-gray-600"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
          </div>
        </div>
      ))}
    </div>
  );
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}